import { useEffect } from 'react';
import { ApiClientError, getAccessToken } from '../api/client';
import { posApi } from '../api/endpoints';
import { currentPerson, onPersonChange } from '../auth/currentPerson';
import { onReconnect } from '../lib/connectivity';
import { countUnsentByRegister } from './sessions';
import { HANDOVER_KEY, PosStore } from './store';
import { SyncEngine } from './sync';
import { createSyncApi } from './syncApi';
import type { RegisterMeta } from './types';

/**
 * Sendet wartende Verkäufe von Kassen dieses Geräts, deren Kassenseite nicht offen ist (U15a). Wer die Kasse verlässt (z. B.
 * zu „Bargeld zählen“) und Netz bekommt, soll nicht darauf warten müssen, dass jemand zur Kasse zurückkehrt: Sonst ist auch
 * „Erwartet“ im Bargeldabgleich zu niedrig.
 *
 * Regeln:
 * - Je Kasse sendet nie mehr als ein Sender: Web Lock `barso-pos-<Basar>` mit `ifAvailable`. Hält die Kassenseite die Sperre
 *   (auch in einem anderen Tab), passiert nichts. Ohne Web Locks wird nicht gesendet (zwei Sender ohne Sperre würden zwar
 *   nichts doppelt verbuchen, aber `seq`-Lücken vortäuschen).
 * - Nur Senden (Outbox in Paketen, danach Kommentare und Fotos), kein Snapshot. Warten nur Kommentare oder Fotos, läuft der
 *   Sender ebenso. Das Protokoll ist unverändert; ein Verkauf verlässt die Outbox erst nach
 *   der Bestätigung des Servers (SYN-02).
 * - Kasse mit Konto: nur mit der gültigen Basar-Sitzung DIESER Kasse und ohne Zugriffstoken (`sessionOnly`). Sonst spräche
 *   die gerade angemeldete Person: Gehört ihr die Kasse nicht (Schichtwechsel ohne Übergabe), antwortet der Server 404 und
 *   die Kasse gälte fälschlich als abgekoppelt; eine abgelehnte Anmeldung würde die Oberfläche abmelden. Fehlt die Sitzung
 *   oder ist sie abgelaufen, sendet erst die Kassenseite (sie erneuert die Sitzung). Eine vom Server abgelehnte Sitzung wird
 *   bis zu ihrer Erneuerung nicht wieder versucht.
 * - Nach dem Abmelden der Besitzerin (ihre Sitzung ist weg) bleiben ihre Verkäufe liegen und gehen erst unter ihrem Konto hinaus:
 *   Meldet sich genau die Besitzerin (`ownerUserId`) wieder an, holt der Sender mit ihrem Zugriffstoken eine neue Sitzung der
 *   Kasse und sendet. Ist jemand anderes angemeldet (oder niemand), geschieht nichts: nie unter fremdem Konto (Regel 10).
 * - Übergeben (Freigabe zur Übernahme gesetzt): Die Kasse gehört niemandem, es wird nicht gesendet.
 * - `DEVICE_REVOKED`: Die Kasse wird als abgekoppelt vermerkt (wie auf der Kassenseite) und nicht weiter versucht.
 */

/** Abstand der Läufe, solange etwas wartet. */
export const BACKGROUND_INTERVAL_MS = 60_000;

export type BackgroundOutcome =
  /** Alles gesendet, nichts wartet mehr. */
  | 'sent'
  /** Es wurde gesendet, aber es wartet noch etwas (Netzfehler oder Zeitlimit mitten im Lauf). */
  | 'partial'
  /** Nichts zu senden. */
  | 'nothing'
  /** Die Kassenseite (oder ein anderer Tab) hält die Sperre: Sie sendet selbst. */
  | 'busy'
  /** Das Gerät ist abgekoppelt oder gesperrt (jetzt oder schon früher vermerkt). */
  | 'revoked'
  /** Fehler (kein Netz, Server nicht erreichbar): Die Warteschlange bleibt unverändert. */
  | 'failed'
  /** Kein Web Locks, kein Senden. */
  | 'unsupported'
  /** Übergeben, ohne gültige Sitzung der Kasse oder Sitzung abgelehnt: kein Senden von hier (die Kassenseite übernimmt). */
  | 'skipped';

export interface BackgroundResult {
  eventId: string;
  outcome: BackgroundOutcome;
}

const running = new Map<string, Promise<BackgroundOutcome>>();
/** Vom Server abgelehnte Sitzungen (401): bis zur Erneuerung durch die Kassenseite nicht wieder versuchen (kein 401 je Minute). */
const rejectedSessions = new Set<string>();
const doneListeners = new Set<() => void>();

/** Wird nach jedem Sendelauf (auch erfolglos) gerufen, damit Anzeigen neu zählen. */
export function onBackgroundSendDone(listener: () => void): () => void {
  doneListeners.add(listener);
  return () => {
    doneListeners.delete(listener);
  };
}

/**
 * Wartet, bis ein laufender Hintergrund-Lauf dieser Kasse in diesem Fenster fertig ist. Die Kassenseite ruft das vor dem
 * Sichern der Sperre auf, damit sie nicht an einem eigenen kurzen Hintergrund-Lauf mit „in anderem Tab geöffnet“ scheitert.
 */
export async function whenBackgroundIdle(eventId: string): Promise<void> {
  await running.get(eventId)?.catch(() => undefined);
}

function sessionUsable(meta: RegisterMeta): boolean {
  const expires =
    meta.sessionExpiresAt === undefined ? Number.NaN : Date.parse(meta.sessionExpiresAt);
  return (
    meta.sessionToken !== undefined &&
    !rejectedSessions.has(meta.sessionToken) &&
    !Number.isNaN(expires) &&
    expires > Date.now()
  );
}

/**
 * Neue Sitzung der Kasse, aber nur wenn die angemeldete Person ihre Besitzerin ist: Der Server erneuert ohnehin nur für sie
 * (sonst 404), hier wird gar nicht erst gefragt. `false`: nicht erneuert (jemand anderes, niemand, kein Netz, abgelehnt).
 */
async function renewForOwner(
  store: PosStore,
  eventId: string,
  meta: RegisterMeta,
): Promise<boolean> {
  if (meta.ownerUserId === undefined || meta.ownerUserId !== currentPerson()) return false;
  if (getAccessToken() === null) return false;
  try {
    const renewed = await posApi.renewSession(eventId, meta.registerId);
    await store.setSession(renewed.sessionToken, renewed.sessionExpiresAt);
    return true;
  } catch {
    return false;
  }
}

async function sendLocked(eventId: string): Promise<BackgroundOutcome> {
  const store = await PosStore.open(`barso-pos-${eventId}`);
  try {
    const meta = await store.register();
    if (meta === undefined) return 'nothing';
    if ((await store.getMeta<boolean>('revoked')) === true) return 'revoked';
    if (meta.mode !== 'name' && typeof (await store.getMeta<unknown>(HANDOVER_KEY)) === 'string') {
      return 'skipped';
    }
    // Mit Konto: nur mit der gültigen Sitzung dieser Kasse (siehe oben), nie mit dem Zugriffstoken. Fehlt sie, kann nur die
    // Besitzerin selbst (angemeldet) eine neue holen.
    if (meta.mode !== 'name' && !sessionUsable(meta)) {
      if (!(await renewForOwner(store, eventId, meta))) return 'skipped';
    }
    const before = await store.pending();
    // Auch nur wartende Kommentare oder Fotos (U17f/U17h) sind ein Anlass: Sie gehen mit demselben Lauf hinaus
    if (before.sales + before.voids === 0 && (await store.pendingSideWork()) === 0) {
      return 'nothing';
    }
    try {
      await new SyncEngine(store, createSyncApi(eventId, store, { sessionOnly: true })).push();
    } catch (error) {
      if (meta.mode !== 'name' && error instanceof ApiClientError && error.status === 401) {
        if (meta.sessionToken !== undefined) rejectedSessions.add(meta.sessionToken);
        return 'skipped';
      }
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        error.code === 'DEVICE_REVOKED'
      ) {
        await store.setMeta('revoked', true).catch(() => undefined);
        return 'revoked';
      }
      // Kein Netz, Zeitlimit, Serverfehler: Die Warteschlange bleibt, der nächste Anstoß versucht es erneut
      return 'failed';
    }
    const after = await store.pending();
    return after.sales + after.voids === 0 ? 'sent' : 'partial';
  } finally {
    store.close();
  }
}

/** Sendet die wartenden Verkäufe EINER Kasse (nur wenn dieses Fenster die Sperre bekommt). */
export function sendRegister(eventId: string): Promise<BackgroundOutcome> {
  const active = running.get(eventId);
  if (active !== undefined) return active;
  const locks: LockManager | undefined = 'locks' in navigator ? navigator.locks : undefined;
  if (locks === undefined) return Promise.resolve('unsupported');
  const job = locks
    .request(`barso-pos-${eventId}`, { ifAvailable: true }, async (lock) => {
      if (lock === null) return 'busy' as const;
      return sendLocked(eventId);
    })
    .catch((): BackgroundOutcome => 'failed')
    .finally(() => {
      running.delete(eventId);
      for (const listener of [...doneListeners]) listener();
    });
  running.set(eventId, job);
  return job;
}

/** Sendet alle Kassen dieses Geräts mit wartenden Vorgängen, eine nach der anderen. */
export async function sendWaitingRegisters(): Promise<BackgroundResult[]> {
  const results: BackgroundResult[] = [];
  for (const entry of await countUnsentByRegister()) {
    if (entry.count === 0 && (entry.side ?? 0) === 0 && !entry.unknown) continue;
    results.push({ eventId: entry.eventId, outcome: await sendRegister(entry.eventId) });
  }
  return results;
}

/**
 * Hängt den Hintergrund-Sender einmal in die Oberfläche: beim Start, bei Netz-Rückkehr, Sichtbarwerden und App-`resume` sowie
 * alle 60 s (die Zählung ist billig; gesendet wird nur, wenn etwas wartet).
 */
export function useBackgroundSend(): void {
  useEffect(() => {
    const run = (): void => {
      sendWaitingRegisters().catch(() => undefined);
    };
    run();
    const off = onReconnect(run);
    const offPerson = watchPersonChange();
    const timer = setInterval(run, BACKGROUND_INTERVAL_MS);
    return () => {
      off();
      offPerson();
      clearInterval(timer);
    };
  }, []);
}

/** Wer sich anmeldet, bekommt seine wartenden Verkäufe sofort gesendet (nicht erst im nächsten Minutentakt). */
export function watchPersonChange(): () => void {
  return onPersonChange(() => {
    sendWaitingRegisters().catch(() => undefined);
  });
}
